// AI Configuration Service
// This service provides a centralized way to get AI model configurations from Firestore
import { db } from '../src/firebase/config';
import { doc, getDoc } from 'firebase/firestore';

export interface AIModelConfig {
  research: string;
  chat: string;
  libraryManagement: string;
  examGeneration: string;
  autoRemarks?: string;
  navigationSearch?: string;
  admissionPattern: string; // New: AI Pattern Creator for Admission Number Setup
  audioAssistant: string; // New: Multimodal Audio Assistant
  // Independent (teach.sonoma.school) platform overrides
  independent_research?: string;
  independent_chat?: string;
  independent_libraryManagement?: string;
  independent_examGeneration?: string;
  independent_autoRemarks?: string;
  independent_navigationSearch?: string;
  independent_admissionPattern?: string;
  independent_audioAssistant?: string;
  isPaused?: boolean;
  customModels?: string[];
  pausedSchools?: string[]; // New field
}

const DEFAULT_CONFIG: AIModelConfig = {
  research: 'gemini-2.0-flash',
  chat: 'gemini-2.0-flash',
  libraryManagement: 'gemini-2.0-flash',
  examGeneration: 'gemini-2.0-flash',
  autoRemarks: 'gemini-2.0-flash',
  navigationSearch: 'gemini-2.0-flash',
  admissionPattern: 'gemini-2.0-flash', // New: Default model for Admission Pattern AI
  audioAssistant: 'gemini-2.0-flash', // Default to 1.5-flash equivalent or best multimodal
  independent_research: 'gemini-2.0-flash',
  independent_chat: 'gemini-2.0-flash',
  independent_libraryManagement: 'gemini-2.0-flash',
  independent_examGeneration: 'gemini-2.0-flash',
  independent_autoRemarks: 'gemini-2.0-flash',
  independent_navigationSearch: 'gemini-2.0-flash',
  independent_admissionPattern: 'gemini-2.0-flash',
  independent_audioAssistant: 'gemini-2.0-flash',
  isPaused: false,
  customModels: [],
  pausedSchools: [],
};

// Cache for AI configuration to avoid repeated Firestore calls
let cachedConfig: AIModelConfig | null = null;
let lastFetchTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get AI model configuration from Firestore with caching
 */
export async function getAIConfig(): Promise<AIModelConfig> {
  const now = Date.now();

  // Return cached config if it's still valid
  if (cachedConfig && (now - lastFetchTime) < CACHE_DURATION) {
    return cachedConfig;
  }

  try {
    const configRef = doc(db, 'systemConfig', 'aiModels');
    const configSnap = await getDoc(configRef);

    if (configSnap.exists()) {
      cachedConfig = { ...DEFAULT_CONFIG, ...configSnap.data() } as AIModelConfig;
    } else {
      cachedConfig = DEFAULT_CONFIG;
    }

    lastFetchTime = now;
    return cachedConfig;
  } catch (error) {
    console.error('Error fetching AI configuration, using defaults:', error);
    return DEFAULT_CONFIG;
  }
}

/**
 * Sanitize model names to prevent 404 errors with retired or preview models.
 */
export function sanitizeModelName(modelName: string): string {
  if (!modelName) return 'gemini-2.0-flash';
  
  const lowerName = modelName.toLowerCase();
  
  // Map retired preview models to stable versions
  if (lowerName.includes('lite-preview') || 
      lowerName.includes('gemini-3') ||
      lowerName.includes('gemini-2.5-flash-preview')) {
    return 'gemini-2.0-flash';
  }
  
  return modelName;
}

/**
 * Get model for a specific feature
 * Throws error if AI features are paused
 */
export async function getModelForFeature(feature: keyof AIModelConfig, schoolId?: string): Promise<string> {
  // Check if AI features are paused (checks global and per-school)
  if (await isSchoolAIPaused(schoolId)) {
    throw new Error('AI features are currently paused. Please contact your administrator.');
  }

  const config = await getAIConfig();

  const modelValue = config[feature];
  if (typeof modelValue === 'string') {
    return sanitizeModelName(modelValue);
  }

  const defaultValue = DEFAULT_CONFIG[feature];
  return sanitizeModelName(typeof defaultValue === 'string' ? defaultValue : 'gemini-2.0-flash');
}

/**
 * Clear the configuration cache (useful when config is updated)
 */
export function clearAIConfigCache(): void {
  cachedConfig = null;
  lastFetchTime = 0;
}

/**
 * Get list of school IDs that have AI paused
 */
export async function getPausedSchools(): Promise<string[]> {
  const config = await getAIConfig();
  return config.pausedSchools || [];
}

/**
 * Check if AI is paused for a specific school
 * Returns true if paused globally OR if paused for this specific school
 */
export async function isSchoolAIPaused(schoolId?: string): Promise<boolean> {
  const config = await getAIConfig();

  // Check global pause first
  if (config.isPaused) {
    console.log('AI paused globally');
    return true;
  }

  // If no school ID provided, check strictly global (already done)
  if (!schoolId) {
    console.log('No school ID provided for AI pause check');
    return false;
  }

  // Check per-school pause from the SAME config object
  const pausedSchools = config.pausedSchools || [];
  const isPaused = pausedSchools.includes(schoolId);
  console.log(`Checking AI pause for school ${schoolId}: ${isPaused ? 'PAUSED' : 'ACTIVE'} (Paused list: ${pausedSchools.join(', ')})`);
  return isPaused;
}

/**
 * Subscribe to AI configuration changes in real-time
 */
export function subscribeToAIConfig(callback: (config: AIModelConfig) => void): () => void {
  const configRef = doc(db, 'systemConfig', 'aiModels');

  // Use Firestore's onSnapshot for real-time updates
  const { onSnapshot } = require('firebase/firestore');

  const unsubscribe = onSnapshot(configRef, (snapshot: any) => {
    if (snapshot.exists()) {
      const config = { ...DEFAULT_CONFIG, ...snapshot.data() } as AIModelConfig;
      cachedConfig = config;
      lastFetchTime = Date.now();
      callback(config);
    } else {
      callback(DEFAULT_CONFIG);
    }
  }, (error: any) => {
    console.error('Error listening to AI configuration:', error);
    callback(DEFAULT_CONFIG);
  });

  return unsubscribe;
}
